'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Combobox, Skeleton } from '@systemeio/components';
import { ChevronDownIcon } from '@systemeio/icons';
import { useMemo, useState, type ReactNode, type RefObject } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useFormControlSize } from '../form-control-size-context';
type FormSelectIdType = string | number | null | undefined;
type FormSelectSize = 'sm' | 'md';
interface FormSelectDataType<T extends FormSelectIdType = FormSelectIdType> {
  additionalRender?: ReactNode;
  caption: string;
  customCaption?: ReactNode;
  dataTestAttribute?: string;
  disabled?: boolean;
  id: T;
}
interface FormSelectProps<T extends FormSelectIdType> {
  ariaLabel?: string;
  data: FormSelectDataType<T>[] | undefined;
  dataTest?: Record<string, string>;
  description?: ReactNode;
  disabled?: boolean;
  error?: ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  isPreFetching?: boolean;
  label?: ReactNode;
  onChange: (value: T | undefined) => void;
  onClose?: () => void;
  onOpen?: () => void;
  placeholder?: string;
  required?: boolean;
  size?: FormSelectSize;
  staticData?: FormSelectDataType<T>[];
  statusIcon?: ReactNode;
  value: T | undefined;
  withoutCloseIcon?: boolean;
}
interface FormSelectMultipleProps<T extends FormSelectIdType> extends Omit<FormSelectProps<T>, 'onChange' | 'value'> {
  chipColor?: 'blue' | 'red';
  createData?: {
    getCustomCaption: (value: string) => string;
    onCreate: (data: string) => Promise<void>;
  };
  defaultOpen?: boolean;
  chipsLayout?: Combobox.Chips.Layout;
  onChange: (value: T[]) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  value: T[];
}
const getSelectedOption = <T extends FormSelectIdType,>(value: T | undefined, data: FormSelectDataType<T>[] | undefined) => data?.find(option => Object.is(option.id, value)) ?? null;
function cancelBackspaceClear(withoutCloseIcon: boolean | undefined, eventDetails: Combobox.Root.ChangeEventDetails | undefined) {
  if (!withoutCloseIcon || !eventDetails) return false;
  const isBackspace = 'key' in eventDetails.event && eventDetails.event.key === 'Backspace' || 'inputType' in eventDetails.event && eventDetails.event.inputType === 'deleteContentBackward';
  if (isBackspace) {
    eventDetails.cancel();
    return true;
  }
  return false;
}
const getOptionId = (option: FormSelectDataType) => String(option.id);

/**
 * Windowing pays for itself only once a list is long enough to cost layout. Below this
 * the virtualized branch is pure overhead: Base UI's virtualized item subscribes to the
 * derived-items context on every keystroke, and the fixed 48px row estimate makes the
 * popup resize on open until rows are measured.
 *
 * Derived from the item count, so it flips at most once — when data arrives. The adaptive
 * Root remounts Base UI on that flip, because Base UI requires `virtualized` to be stable
 * for an item's lifetime.
 */
const VIRTUALIZE_FROM_ITEMS = 100;
const shouldVirtualize = (data: unknown[] | undefined) => (data?.length ?? 0) >= VIRTUALIZE_FROM_ITEMS;

/**
 * The create row lives outside `items`: `items` is also the filter collection and the
 * label registry, so a synthetic entry there leaks into both.
 */
const CREATE_OPTION_ID = '__create-option';
interface CreateOption {
  caption: string;
  create: true;
  id: typeof CREATE_OPTION_ID;
}
const isCreateOption = <T extends FormSelectIdType,>(option: FormSelectDataType<T> | CreateOption): option is CreateOption => 'create' in option;
const makeCreateOption = (caption: string): CreateOption => ({
  caption,
  create: true,
  id: CREATE_OPTION_ID
});

/** Only options whose caption differs from the query can be created. */
function getCreatableQuery<T extends FormSelectIdType>(search: string, data: FormSelectDataType<T>[] | undefined) {
  const trimmed = search.trim();
  if (!trimmed) return null;
  const hasExactMatch = (data ?? []).some(option => option.caption.trim().toLowerCase() === trimmed.toLowerCase());
  return hasExactMatch ? null : trimmed;
}
function OptionsSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="grid gap-2 p-3"><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-4/5" /><Skeleton className="h-5 w-3/5" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function FormSelect(t0) {
  const $ = _c(67);
  const {
    ariaLabel,
    data,
    dataTest,
    description,
    disabled,
    error,
    inputRef,
    isPreFetching,
    label,
    onChange,
    onClose,
    onOpen,
    placeholder,
    required,
    size,
    staticData,
    statusIcon,
    value,
    withoutCloseIcon
  } = t0;
  const inheritedSize = useFormControlSize();
  const {
    t
  } = useLocoTranslation();
  const t1 = data ?? staticData;
  let t2;
  if ($[0] !== t1 || $[1] !== value) {
    t2 = getSelectedOption(value, t1);
    $[0] = t1;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selectedOption = t2;
  const isDisabled = disabled || isPreFetching || value !== undefined && !data && !staticData;
  let t3;
  if ($[3] !== placeholder || $[4] !== t) {
    t3 = placeholder ?? t("global.select");
    $[3] = placeholder;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const resolvedPlaceholder = t3;
  const accessibleLabel = ariaLabel ?? (typeof label === "string" ? label : resolvedPlaceholder);
  const t4 = isPreFetching || undefined;
  const t5 = label ?? resolvedPlaceholder;
  let t6;
  if ($[6] !== data) {
    t6 = data ?? [];
    $[6] = data;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onClose || $[9] !== onOpen) {
    t7 = open => {
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    };
    $[8] = onClose;
    $[9] = onOpen;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onChange || $[12] !== withoutCloseIcon) {
    t8 = (option_2, eventDetails) => {
      if (cancelBackspaceClear(withoutCloseIcon, eventDetails)) {
        return;
      }
      onChange(option_2?.id);
    };
    $[11] = onChange;
    $[12] = withoutCloseIcon;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = size ?? inheritedSize;
  let t10;
  if ($[14] !== data) {
    t10 = shouldVirtualize(data);
    $[14] = data;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== label || $[17] !== required) {
    t11 = label !== undefined ? <Combobox.InputLabel required={required}>{label}</Combobox.InputLabel> : null;
    $[16] = label;
    $[17] = required;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = label === undefined ? accessibleLabel : undefined;
  const t13 = isPreFetching ? "animate-pulse" : undefined;
  const t14 = error ? "" : undefined;
  let t15;
  if ($[19] !== dataTest || $[20] !== inputRef) {
    t15 = <Combobox.Input {...dataTest} ref={inputRef} />;
    $[19] = dataTest;
    $[20] = inputRef;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== statusIcon) {
    t16 = statusIcon ? <span className="text-field-icon flex size-5 shrink-0 items-center justify-center">{statusIcon}</span> : null;
    $[22] = statusIcon;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== isDisabled || $[25] !== selectedOption || $[26] !== t || $[27] !== withoutCloseIcon) {
    t17 = !withoutCloseIcon && selectedOption && !isDisabled ? <Combobox.Clear aria-label={t("global.clear")} /> : null;
    $[24] = isDisabled;
    $[25] = selectedOption;
    $[26] = t;
    $[27] = withoutCloseIcon;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ChevronDownIcon />;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== accessibleLabel) {
    t19 = <Combobox.Trigger aria-label={accessibleLabel}>{t18}</Combobox.Trigger>;
    $[30] = accessibleLabel;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17 || $[38] !== t19) {
    t20 = <Combobox.InputGroup aria-label={t12} className={t13} data-invalid={t14}>{t15}{t16}{t17}{t19}</Combobox.InputGroup>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== data || $[41] !== t) {
    t21 = <Combobox.Content>{data === undefined ? <OptionsSkeleton /> : <><Combobox.Empty description={t("dashboard.droplist.default_description")}>{t("dashboard.droplist.default_title")}</Combobox.Empty><Combobox.List>{_temp4}</Combobox.List></>}</Combobox.Content>;
    $[40] = data;
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== accessibleLabel || $[44] !== isDisabled || $[45] !== required || $[46] !== resolvedPlaceholder || $[47] !== selectedOption || $[48] !== t10 || $[49] !== t11 || $[50] !== t20 || $[51] !== t21 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t22 = <Combobox.Root aria-label={accessibleLabel} disabled={isDisabled} drawerTitle={t5} isItemEqualToValue={_temp} itemToStringLabel={_temp2} itemToStringValue={_temp3} items={t6} onOpenChange={t7} onValueChange={t8} placeholder={resolvedPlaceholder} required={required} size={t9} value={selectedOption} virtualized={t10}>{t11}{t20}{t21}</Combobox.Root>;
    $[43] = accessibleLabel;
    $[44] = isDisabled;
    $[45] = required;
    $[46] = resolvedPlaceholder;
    $[47] = selectedOption;
    $[48] = t10;
    $[49] = t11;
    $[50] = t20;
    $[51] = t21;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== description) {
    t23 = description ? <Combobox.Description>{description}</Combobox.Description> : null;
    $[58] = description;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== error) {
    t24 = error ? <Combobox.Error>{error}</Combobox.Error> : null;
    $[60] = error;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t22 || $[63] !== t23 || $[64] !== t24 || $[65] !== t4) {
    t25 = <div aria-busy={t4} className="grid w-full content-start gap-2">{t22}{t23}{t24}</div>;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
    $[65] = t4;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  return t25;
}
function _temp4(option_3) {
  return <Combobox.Item data-test-id={option_3.dataTestAttribute} disabled={option_3.disabled} key={getOptionId(option_3)} value={option_3}><Combobox.ItemText>{option_3.customCaption ?? option_3.caption}</Combobox.ItemText>{option_3.additionalRender}<Combobox.ItemIndicator /></Combobox.Item>;
}
function _temp3(option_1) {
  return String(option_1.id);
}
function _temp2(option_0) {
  return option_0.caption;
}
function _temp(option, selected) {
  return Object.is(option.id, selected.id);
}
function FormSelectMultiple<T extends FormSelectIdType>({
  ariaLabel,
  chipColor,
  chipsLayout,
  createData,
  data,
  dataTest,
  defaultOpen,
  description,
  disabled,
  error,
  inputRef,
  isPreFetching,
  label,
  onChange,
  onClose,
  onOpen,
  onOpenChange,
  open,
  placeholder,
  required,
  size,
  staticData,
  value,
  withoutCloseIcon
}: FormSelectMultipleProps<T>) {
  const inheritedSize = useFormControlSize();
  const {
    t
  } = useLocoTranslation();
  // Mirrored, not controlled: Base UI owns the input so it can derive the desktop label
  // from the selection, and the mobile drawer search opens empty.
  const [searchValue, setSearchValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const selectedOptions = useMemo(() => value.map(selectedValue => getSelectedOption(selectedValue, data ?? staticData)).filter((option): option is FormSelectDataType<T> => option !== null), [data, staticData, value]);
  const selectedIds = new Set(value);
  const isDisabled = disabled || isPreFetching || isCreating || value.length > 0 && !data && !staticData;
  const resolvedPlaceholder = placeholder ?? t('global.select');
  const accessibleLabel = ariaLabel ?? (typeof label === 'string' ? label : resolvedPlaceholder);
  const creatableQuery = createData ? getCreatableQuery(searchValue, data) : null;
  const onCreate = async (caption: string) => {
    setIsCreating(true);
    try {
      await createData?.onCreate(caption);
      setSearchValue('');
    } finally {
      setIsCreating(false);
    }
  };
  return <div aria-busy={isPreFetching || isCreating || undefined} className="grid w-full content-start gap-2">
      <Combobox.Root<FormSelectDataType<T> | CreateOption, true> aria-label={accessibleLabel} defaultOpen={defaultOpen} disabled={isDisabled} drawerTitle={label ?? resolvedPlaceholder} isItemEqualToValue={(option_0, selected) => Object.is(option_0.id, selected.id)} itemToStringLabel={option_1 => option_1.caption} itemToStringValue={option_2 => String(option_2.id)} items={data ?? []} multiple onOpenChange={nextOpen => {
      onOpenChange?.(nextOpen);
      if (nextOpen) onOpen?.();else onClose?.();
    }} open={open} onSearchChange={setSearchValue} onValueChange={(options, eventDetails) => {
      if (cancelBackspaceClear(withoutCloseIcon, eventDetails)) return;
      const next = options ?? [];
      const created = next.find(isCreateOption);
      if (created) {
        void onCreate(created.caption);
        return;
      }
      onChange(next.map(option_3 => option_3.id as T));
    }} placeholder={resolvedPlaceholder} required={required} size={size ?? inheritedSize} value={selectedOptions} virtualized={shouldVirtualize(data)}>
        {label !== undefined ? <Combobox.InputLabel required={required}>{label}</Combobox.InputLabel> : null}
        <Combobox.InputGroup aria-label={label === undefined ? accessibleLabel : undefined} className={isPreFetching || isCreating ? 'animate-pulse' : undefined} data-invalid={error ? '' : undefined}>
          {selectedOptions.length > 0 ? <Combobox.Chips layout={chipsLayout}>
              {selectedOptions.map(option_4 => <Combobox.Chip id={getOptionId(option_4)} key={getOptionId(option_4)} render={<Badge color={chipColor ?? 'blue'} size={(size ?? inheritedSize) === 'sm' ? 'md' : 'lg'} />}>
                  {option_4.caption}
                  <Combobox.ChipRemove aria-label={`${t('global.delete')} ${option_4.caption}`} />
                </Combobox.Chip>)}
              <Combobox.OverflowBadge color={chipColor} />
              {/* Empty placeholder: the mobile field already shows the chips. */}
              <Combobox.Input {...dataTest} placeholder="" ref={inputRef} />
            </Combobox.Chips> : <Combobox.Input {...dataTest} ref={inputRef} />}
          {!withoutCloseIcon && selectedOptions.length > 0 && !isDisabled ? <Combobox.Clear aria-label={t('global.clear')} /> : null}
          <Combobox.Trigger aria-label={accessibleLabel}>
            <ChevronDownIcon />
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Content>
          {data === undefined ? <OptionsSkeleton /> : <>
              <Combobox.Empty description={t('dashboard.droplist.default_description')}>
                {t('dashboard.droplist.default_title')}
              </Combobox.Empty>
              <Combobox.List>
                {(option_5: FormSelectDataType<T>) => <Combobox.Item data-test-id={option_5.dataTestAttribute} disabled={option_5.disabled} key={getOptionId(option_5)} value={option_5}>
                    <Combobox.ItemCheckbox checked={selectedIds.has(option_5.id)} />
                    <Combobox.ItemText>{option_5.customCaption ?? option_5.caption}</Combobox.ItemText>
                    {option_5.additionalRender}
                  </Combobox.Item>}
              </Combobox.List>
            </>}
          {creatableQuery ? <Combobox.CreateOption value={makeCreateOption(creatableQuery)}>
              {createData?.getCustomCaption(creatableQuery)}
            </Combobox.CreateOption> : null}
        </Combobox.Content>
      </Combobox.Root>
      {description ? <Combobox.Description>{description}</Combobox.Description> : null}
      {error ? <Combobox.Error>{error}</Combobox.Error> : null}
    </div>;
}
export { cancelBackspaceClear, FormSelect, FormSelectMultiple, getCreatableQuery, getSelectedOption, isCreateOption, makeCreateOption, OptionsSkeleton, shouldVirtualize, VIRTUALIZE_FROM_ITEMS };
export type { CreateOption, FormSelectDataType, FormSelectIdType, FormSelectMultipleProps, FormSelectProps, FormSelectSize };